"use client";

// Shared MCP connection form fields — ONE form for the Add MCP modal
// (designs 7897-107469…7901-111185) and the Edit MCP drawer (8140-122253):
// Connector Name / Description / Server URL (+ probe feedback) and the
// Advance Settings section holding custom headers + the OAuth client block.
//
// Presentational + controlled: the caller owns every persisted value and the
// probe/submit pipeline; UI-only state (secret visibility, copied flags,
// guided-steps expansion) lives here. `variant` encodes the two designs'
// differences:
//   add  — OAuth box always present inside Advance Settings (optional label
//          when credentials are not required), with the admin-approval badge
//          variant.
//   edit — OAuth hint + how-to link under the Server URL; OAuth box only when
//          relevant.
// Both variants share the Callback URL field (read-only box + inline copy
// icon) via CallbackUrlField.

import { useEffect, useState, type ReactNode } from "react";
import { useTranslations } from "next-intl";
import { ChevronDown, Copy, Eye, EyeOff, Loader2, Plus, X } from "lucide-react";
import { Input } from "@/components/ui";
import { domId } from "@/lib/dom-id";
import type { McpConnectionMode } from "@/lib/mcp-auth-derive";
import { getMcpOAuthCallbackUrl } from "@/services/mcp-service";
import {
  emptyCustomHeader,
  type CustomHeader,
} from "@/components/mcp/custom-headers-input";
import { CallbackUrlField } from "@/components/mcp/callback-url-field";

// Keeps the part of an edit that fits the name limit, as a native maxlength
// keeps what fits of a paste, but counting code points like the limit check.
// An edit that does not lengthen the name (e.g. shortening an older, longer
// one) passes as is. `caret` is where the kept text ends.
function fitNameEdit(previous: string, next: string, max: number) {
  const nextChars = Array.from(next);
  const prevChars = Array.from(previous);
  if (nextChars.length <= max || nextChars.length <= prevChars.length) {
    return { value: next, cut: false, caret: next.length };
  }
  let start = 0;
  while (
    start < prevChars.length &&
    start < nextChars.length &&
    prevChars[start] === nextChars[start]
  ) {
    start += 1;
  }
  let end = 0;
  while (
    end < prevChars.length - start &&
    end < nextChars.length - start &&
    prevChars[prevChars.length - 1 - end] === nextChars[nextChars.length - 1 - end]
  ) {
    end += 1;
  }
  const inserted = nextChars.slice(start, nextChars.length - end);
  const room = max - (nextChars.length - inserted.length);
  const head = [...nextChars.slice(0, start), ...inserted.slice(0, Math.max(0, room))].join("");
  return {
    value: head + nextChars.slice(nextChars.length - end).join(""),
    cut: true,
    caret: head.length,
  };
}

export const mcpFieldLabelClass =
  "block text-base font-medium leading-[1.4] text-[#0B0809]";
export const mcpInputClass =
  "h-10 w-full rounded-lg border border-[#DCDFE3] bg-white px-4 text-sm text-[#00152C] placeholder:text-[#8E9BA8] focus:border-[#713D98] focus:outline-none focus:ring-2 focus:ring-[#713D98]/10";

export interface McpConnectionFormFieldsProps {
  purpose: string;
  variant: "add" | "edit";
  disabled: boolean;

  name: string;
  onNameChange: (v: string) => void;
  nameMaxLength?: number;
  nameError?: string | null;
  onNameLimitExceeded?: () => void;
  description: string;
  onDescriptionChange: (v: string) => void;
  url: string;
  onUrlChange: (v: string) => void;
  onUrlClear: () => void;
  onUrlFocusChange?: (focused: boolean) => void;
  urlError: string | null;
  probing: boolean;

  advanceOpen: boolean;
  onAdvanceOpenChange: (open: boolean) => void;
  headers: CustomHeader[];
  onHeadersChange: (rows: CustomHeader[]) => void;

  /** Slot rendered between the Server URL field and the Advance Settings
   *  section — e.g. the amber setup-instruction box. */
  beforeAdvanced?: ReactNode;
  connectionMode?: McpConnectionMode;
  onConnectionModeChange?: (mode: McpConnectionMode) => void;

  /** OAuth credentials block is highlighted as required by the probe. */
  showOauthSection: boolean;
  /** Client ID / Secret carry required markers and gate the submit. */
  credsRequired: boolean;
  /** Add-flow only: probe says an org admin must approve the OAuth app. */
  adminApproval?: boolean;
  clientId: string;
  onClientIdChange: (v: string) => void;
  clientSecret: string;
  onClientSecretChange: (v: string) => void;

  /** Optional autofocus on the name field (add modal). */
  autoFocusName?: boolean;
}

export function McpConnectionFormFields({
  purpose,
  variant,
  disabled,
  name,
  onNameChange,
  nameMaxLength,
  nameError,
  onNameLimitExceeded,
  description,
  onDescriptionChange,
  url,
  onUrlChange,
  onUrlClear,
  onUrlFocusChange,
  urlError,
  probing,
  advanceOpen,
  onAdvanceOpenChange,
  headers,
  onHeadersChange,
  beforeAdvanced,
  connectionMode,
  onConnectionModeChange,
  showOauthSection,
  credsRequired,
  adminApproval = false,
  clientId,
  onClientIdChange,
  clientSecret,
  onClientSecretChange,
  autoFocusName = false,
}: McpConnectionFormFieldsProps) {
  const t = useTranslations("Mcp.addModal");
  const tEdit = useTranslations("Mcp.editPanel");

  // UI-only state.
  const [showSecret, setShowSecret] = useState(false);
  const [howToOpen, setHowToOpen] = useState(false);
  const [redirectUri, setRedirectUri] = useState("");
  const [copiedInstruction, setCopiedInstruction] = useState(false);

  useEffect(() => {
    getMcpOAuthCallbackUrl()
      .then(setRedirectUri)
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!copiedInstruction) return;
    const timer = setTimeout(() => setCopiedInstruction(false), 1500);
    return () => clearTimeout(timer);
  }, [copiedInstruction]);

  const handleCopyInstruction = () => {
    const steps = [
      `1. ${t("steps.register.title")} — ${t("steps.register.appName")}; ${t(
        "steps.register.redirectUri",
        { uri: redirectUri },
      )}`,
      `2. ${t("steps.approval.title")} — ${t("steps.approval.desc")}`,
      `3. ${t("steps.connect.title")} — ${t("steps.connect.desc")}`,
    ].join("\n");
    void navigator.clipboard?.writeText(steps);
    setCopiedInstruction(true);
  };

  const updateHeader = (idx: number, patch: Partial<CustomHeader>) => {
    const next = headers.slice();
    next[idx] = { ...next[idx], ...patch };
    onHeadersChange(next);
  };

  const requiredStar = <span className="text-[#DC2626]">*</span>;

  const oauthBadge = (
    <span
      data-testid={domId(
        "sec",
        `${purpose}-${adminApproval ? "admin-approval" : "oauth"}-badge`,
      )}
      className="inline-flex items-center gap-1 self-start rounded-full border border-[#FFEACB] bg-[#FDF7E6] px-2 py-0.5"
    >
      <span className="h-1.5 w-1.5 rounded-full bg-[#EAB308]" />
      <span className="text-[10px] font-bold leading-[1.4] text-[#D97F00]">
        {adminApproval ? t("adminApproval.badge") : t("oauth.badge")}
      </span>
    </span>
  );

  // Guided steps — design d05 hidden frame 7995-118056.
  const guidedSteps = (
    <div
      data-testid={domId("sec", `${purpose}-howto-steps`)}
      className="flex flex-col gap-2"
    >
      <ol className="flex w-full flex-col gap-3 rounded-lg bg-white p-2">
        {(
          [
            {
              key: "register",
              title: t("steps.register.title"),
              lines: [
                t("steps.register.appName"),
                t("steps.register.redirectUri", { uri: redirectUri }),
              ],
            },
            {
              key: "approval",
              title: t("steps.approval.title"),
              lines: [t("steps.approval.desc")],
            },
            {
              key: "connect",
              title: t("steps.connect.title"),
              lines: [t("steps.connect.desc")],
            },
          ] as const
        ).map((step, idx) => (
          <li key={step.key} className="flex w-full items-start">
            <span className="flex w-8 shrink-0 pr-3 pt-0.5">
              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-[#E5CEFA]">
                <span className="bg-gradient-to-r from-[#713D98] to-[#3E3D98] bg-clip-text text-center text-xs font-medium leading-[1.4] text-transparent">
                  {idx + 1}
                </span>
              </span>
            </span>
            <span className="text-sm leading-[1.4] text-[#4C6075]">
              <span className="font-semibold">{step.title}</span>
              {step.lines.map((line) => (
                <span key={line} className="block font-normal">
                  {line}
                </span>
              ))}
            </span>
          </li>
        ))}
      </ol>
      <button
        type="button"
        data-testid={domId("btn", `${purpose}-copy-instruction`)}
        onClick={handleCopyInstruction}
        className="flex h-7 items-center justify-center gap-2 self-start rounded-lg px-3 text-xs font-medium leading-[1.4] text-[#025ECA] hover:bg-[#025ECA]/5"
      >
        <Copy size={14} aria-hidden="true" />
        {copiedInstruction ? t("oauth.copied") : t("copyInstruction")}
      </button>
    </div>
  );

  const howToToggle = (
    <button
      type="button"
      data-testid={domId("btn", `${purpose}-howto-toggle`)}
      onClick={() => setHowToOpen((v) => !v)}
      className="self-start text-[10px] leading-[1.4] text-[#026EEE] underline decoration-solid hover:text-[#025ECA]"
    >
      {t("oauth.howTo")}
    </button>
  );

  let oauthBox: ReactNode = null;
  if (connectionMode !== "direct" && (variant === "add" || showOauthSection)) {
    oauthBox = (
      <div
        data-testid={domId("sec", `${purpose}-oauth`)}
        className="flex flex-col gap-2 rounded-lg border border-[#DCDFE3] p-3"
      >
        {showOauthSection ? (
          <div className="flex flex-col items-start gap-1">
            {oauthBadge}
            {adminApproval && (
              <p className="text-xs leading-[1.4] text-[#4C6075]">
                {t("adminApproval.text")}
              </p>
            )}
            {variant === "add" && howToToggle}
          </div>
        ) : (
          <div className="flex flex-col items-start gap-0.5">
            <span className="text-sm font-medium leading-[1.4] text-[#444746]">
              {t("oauth.sectionLabel")}
            </span>
            <p className="text-xs leading-[1.4] text-[#4C6075]">
              {t("oauth.optionalHint")}
            </p>
          </div>
        )}

        {variant === "add" && showOauthSection && howToOpen && guidedSteps}

        {/* Callback URL — read-only box with an inline copy icon. Shared by the
            add flow and the edit drawer (copy to the provider while registering
            the app). */}
        {redirectUri && <CallbackUrlField purpose={purpose} url={redirectUri} />}

        {/* Client ID */}
        <div className="flex flex-col gap-2">
          <label
            className={mcpFieldLabelClass}
            htmlFor={domId("txt", `${purpose}-client-id`)}
          >
            {t("oauth.clientId.label")} {credsRequired && requiredStar}
          </label>
          <Input
            id={domId("txt", `${purpose}-client-id`)}
            data-testid={domId("txt", `${purpose}-client-id`)}
            type="text"
            autoComplete="off"
            spellCheck={false}
            value={clientId}
            onChange={(e) => onClientIdChange(e.target.value)}
            placeholder={t("oauth.clientId.placeholder")}
            className={mcpInputClass}
            disabled={disabled}
          />
        </div>

        {/* Client Secret — masked with visibility toggle (AC) */}
        <div className="flex flex-col gap-2">
          <label
            className={mcpFieldLabelClass}
            htmlFor={domId("txt", `${purpose}-client-secret`)}
          >
            {t("oauth.clientSecret.label")} {credsRequired && requiredStar}
          </label>
          <div className="relative">
            <Input
              id={domId("txt", `${purpose}-client-secret`)}
              data-testid={domId("txt", `${purpose}-client-secret`)}
              type={showSecret ? "text" : "password"}
              autoComplete="off"
              spellCheck={false}
              value={clientSecret}
              onChange={(e) => onClientSecretChange(e.target.value)}
              placeholder={t("oauth.clientSecret.placeholder")}
              className={`${mcpInputClass} pr-10`}
              disabled={disabled}
            />
            <button
              type="button"
              aria-label={
                showSecret
                  ? t("oauth.clientSecret.hide")
                  : t("oauth.clientSecret.show")
              }
              data-testid={domId("btn", `${purpose}-secret-toggle`)}
              onClick={() => setShowSecret((v) => !v)}
              className="absolute right-2 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-lg text-[#6D7E91] hover:bg-gray-100"
            >
              {showSecret ? <EyeOff size={14} /> : <Eye size={14} />}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Connector Name */}
      <div className="flex flex-col gap-2">
        <label className={mcpFieldLabelClass} htmlFor={domId("txt", `${purpose}-name`)}>
          {t("fields.name.label")} {requiredStar}
        </label>
        <div className="relative">
          <Input
            id={domId("txt", `${purpose}-name`)}
            data-testid={domId("txt", `${purpose}-name`)}
            type="text"
            autoFocus={autoFocusName}
            value={name}
            onChange={(e) => {
              if (typeof nameMaxLength !== "number") {
                onNameChange(e.target.value);
                return;
              }
              // Past the limit, the edit keeps only what fits (a long paste is
              // cut, a keystroke is dropped) and the limit error shows.
              const input = e.currentTarget;
              const fitted = fitNameEdit(name, input.value, nameMaxLength);
              onNameChange(fitted.value);
              if (fitted.cut) {
                onNameLimitExceeded?.();
                requestAnimationFrame(() => input.setSelectionRange(fitted.caret, fitted.caret));
              }
            }}
            placeholder={t("fields.name.placeholder")}
            className={`${mcpInputClass} ${name ? "pr-10" : ""} ${
              nameError ? "border-red-500 focus:border-red-600 focus:ring-red-100" : ""
            }`}
            disabled={disabled}
            aria-invalid={Boolean(nameError)}
            aria-describedby={nameError ? domId("sec", `${purpose}-name-error`) : undefined}
          />
          {name && !disabled && (
            <button
              type="button"
              aria-label={t("actions.clear")}
              data-testid={domId("btn", `${purpose}-name-clear`)}
              onClick={() => onNameChange("")}
              className="absolute right-2 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-lg text-[#6D7E91] hover:bg-gray-100"
            >
              <X size={14} />
            </button>
          )}
        </div>
        {nameError && (
          <p
            id={domId("sec", `${purpose}-name-error`)}
            className="text-xs text-red-600"
          >
            {nameError}
          </p>
        )}
      </div>

      {/* Description (Optional) */}
      <div className="flex flex-col gap-2">
        <label
          className={mcpFieldLabelClass}
          htmlFor={domId("txt", `${purpose}-description`)}
        >
          {t("fields.description.label")}{" "}
          <span className="font-medium text-[#4C6075]">{t("optionalTag")}</span>
        </label>
        <Input
          id={domId("txt", `${purpose}-description`)}
          data-testid={domId("txt", `${purpose}-description`)}
          type="text"
          value={description}
          onChange={(e) => onDescriptionChange(e.target.value)}
          placeholder={t("fields.description.placeholder")}
          className={mcpInputClass}
          disabled={disabled}
        />
      </div>

      {/* Server URL */}
      <div className="flex flex-col gap-1">
        <label className={mcpFieldLabelClass} htmlFor={domId("txt", `${purpose}-url`)}>
          {t("fields.url.label")} {requiredStar}
        </label>
        <div className="relative">
          <Input
            id={domId("txt", `${purpose}-url`)}
            data-testid={domId("txt", `${purpose}-url`)}
            type="text"
            inputMode="url"
            autoComplete="off"
            spellCheck={false}
            value={url}
            onChange={(e) => onUrlChange(e.target.value)}
            onFocus={() => onUrlFocusChange?.(true)}
            onBlur={() => onUrlFocusChange?.(false)}
            placeholder={t("fields.url.placeholder")}
            className={`${mcpInputClass} ${urlError ? "border-[#DC2626] focus:border-[#DC2626] focus:ring-[#DC2626]/10" : ""} ${url ? "pr-10" : ""}`}
            disabled={disabled}
          />
          {url && !disabled && (
            <button
              type="button"
              aria-label={t("actions.clear")}
              data-testid={domId("btn", `${purpose}-url-clear`)}
              onClick={onUrlClear}
              className="absolute right-2 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-lg text-[#6D7E91] hover:bg-gray-100"
            >
              <X size={14} />
            </button>
          )}
        </div>
        {urlError && (
          <p
            data-testid={domId("sec", `${purpose}-url-error`)}
            className="text-xs leading-[1.4] text-[#DC2626]"
          >
            {urlError}
          </p>
        )}
        {probing && (
          <p
            data-testid={domId("sec", `${purpose}-detecting`)}
            className="flex items-center gap-1.5 text-xs leading-[1.4] text-[#4C6075]"
          >
            <Loader2 size={12} className="animate-spin" />
            {t("detecting")}
          </p>
        )}
        {/* Edit drawer (design 8225-229944): oauth hint + how-to live under
            the URL field instead of inside the OAuth box. */}
        {variant === "edit" && showOauthSection && !urlError && (
          <>
            <p className="text-[10px] leading-[1.4] text-[#4C6075]">
              {tEdit("oauthNotice")}
            </p>
            {howToToggle}
            {howToOpen && guidedSteps}
          </>
        )}
      </div>

      {beforeAdvanced}

      {connectionMode && onConnectionModeChange && (
        <fieldset disabled={disabled} className="flex flex-col gap-2">
          <legend className={`${mcpFieldLabelClass} mb-2`}>
            {tEdit("authMode.label")}
          </legend>
          {(["auto", "direct"] as const).map((mode) => (
            <label key={mode} className="flex items-start gap-2 text-sm text-[#00152C]">
              <input
                type="radio"
                name={`${purpose}-auth-mode`}
                data-testid={domId("rad", `${purpose}-auth-${mode === "auto" ? "oauth" : "direct"}`)}
                value={mode}
                checked={connectionMode === mode}
                onChange={() => onConnectionModeChange(mode)}
                className="mt-0.5 accent-[#713D98]"
              />
              {mode === "auto" ? tEdit("authMode.auto") : tEdit("authMode.direct")}
            </label>
          ))}
          {connectionMode === "direct" && (
            <p className="text-xs leading-[1.4] text-[#4C6075]">
              {tEdit("authMode.directHint")}
            </p>
          )}
        </fieldset>
      )}

      {/* Advance Settings (optional) — OAuth client block first, then custom
          headers (feedback 2026-06-12). */}
      <div className="flex flex-col gap-2 py-1">
        <button
          type="button"
          data-testid={domId("btn", `${purpose}-advance-toggle`)}
          onClick={() => onAdvanceOpenChange(!advanceOpen)}
          className="flex w-full items-center gap-1 text-left"
        >
          <ChevronDown
            size={16}
            className={`shrink-0 text-[#444746] transition-transform duration-200 ${advanceOpen ? "" : "-rotate-90"}`}
            aria-hidden="true"
          />
          <span className="text-sm font-medium leading-[1.4] text-[#444746]">
            {t("advance.label")}{" "}
            <span className="text-[#4C6075]">{t("advance.optionalTag")}</span>
          </span>
          {/* Collapsed-state hint: keep the requirement visible even if the
              user manually folds the section back up. */}
          {showOauthSection && !advanceOpen && oauthBadge}
        </button>

        {advanceOpen && (
          <div className="flex flex-col gap-3">
            {oauthBox}

            {/* Custom headers */}
            <div
              data-testid={domId("sec", `${purpose}-headers`)}
              className="flex flex-col gap-2"
            >
              <span className="text-sm font-medium leading-[1.4] text-[#444746]">
                {t("headers.sectionLabel")}
              </span>
              {headers.map((row, idx) => (
                <div
                  key={row.id}
                  className="flex w-full items-center gap-4"
                  data-testid={domId("sec", `${purpose}-header-row-${idx}`)}
                >
                  <input
                    placeholder={t("headers.namePlaceholder")}
                    disabled={disabled}
                    value={row.name}
                    onChange={(e) => updateHeader(idx, { name: e.target.value })}
                    data-testid={domId("txt", `${purpose}-header-name-${idx}`)}
                    className={`min-w-0 flex-1 ${mcpInputClass}`}
                  />
                  <input
                    type="password"
                    autoComplete="new-password"
                    placeholder={
                      row.has_stored_value
                        ? "••••••••"
                        : t("headers.valuePlaceholder")
                    }
                    disabled={disabled}
                    value={row.value}
                    onChange={(e) => updateHeader(idx, { value: e.target.value })}
                    data-testid={domId("txt", `${purpose}-header-value-${idx}`)}
                    className={`min-w-0 flex-1 ${mcpInputClass}`}
                  />
                  <button
                    type="button"
                    aria-label={t("headers.remove")}
                    disabled={disabled}
                    data-testid={domId("btn", `${purpose}-header-delete-${idx}`)}
                    onClick={() =>
                      onHeadersChange(headers.filter((_, i) => i !== idx))
                    }
                    className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg text-[#6D7E91] hover:bg-red-50 hover:text-[#DC2626]"
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
              <button
                type="button"
                data-testid={domId("btn", `${purpose}-header-add`)}
                disabled={disabled}
                onClick={() => onHeadersChange([...headers, emptyCustomHeader()])}
                className="flex h-7 items-center justify-center gap-2 self-start rounded-lg px-3 text-xs font-medium leading-[1.4] text-[#025ECA] hover:bg-[#025ECA]/5"
              >
                <Plus size={14} aria-hidden="true" />
                {t("headers.add")}
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
